import {
  Autocomplete,
  Chip,
  FormControl,
  LinearProgress,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import { DatePicker } from "rsuite";
import CircularIndeterminate from "components/MDLoading";
import { useMaterialUIController } from "context";
import { useDispatch, useSelector } from "react-redux";
import * as XLSX from "xlsx";
// import DownloadIcon from "@mui/icons-material/Download";
import { useState, useMemo, useEffect } from "react";
import { amountToShow } from "global/functions";
import MDSnackbar from "components/MDSnackbar";
import PlacePicker from "components/MDPlacePicker/PlacePicker";
import MDDataGrid from "components/MDDataGrid/MDDataGrid";
import EmptyTableRowOverlay from "components/MDEmptyOverlay/EmptyTableRowOverlay";
import { getRevenueSummary } from "store/slice/reports/reportSlice";
import React from "react";
import "./revenue.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import { uniqBy } from "lodash";
import MDInput from "components/MDInput";

function RevenueSummary() {
  const [controller] = useMaterialUIController();
  const dispatch = useDispatch();
  const { sidenavColor } = controller;
  // Pagination removed
  const [reservations, setReservations] = useState([]);
  const loading = useSelector((state) => state.report?.loading);
  const allPlaces = useSelector((state) => state.places?.placesList);
  const selectedPlace = useSelector((state) => state.places?.selectedPlace);
  let placeId = selectedPlace?._id;
  if (!placeId) {
    placeId = localStorage.getItem("placeId");
  }
  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification({ ...notification, show: false }),
  });
  // Build timezone options similar to TransactionReport
  const timeZones = uniqBy(allPlaces, "timeZoneId").map(
    (place) => place?.timeZoneId || ""
  );
  if (!timeZones.includes("Asia/Kolkata")) timeZones.push("Asia/Kolkata");
  if (!timeZones.includes("UTC")) timeZones.push("UTC");
  const currentDate = new Date();
  const lastWeekDate = new Date(
    currentDate.getTime() - 7 * 24 * 60 * 60 * 1000
  );
  const [filterOptions, setFilterOptions] = useState({
    tz: "UTC",
    placeId,
    startDate: lastWeekDate,
    endDate: currentDate,
  });
  const [downloadAble, setDownloadAble] = useState(false);

  useEffect(() => {
    setFilterOptions((prev) => ({
      ...prev,
      placeId: selectedPlace?._id,
    }));
  }, [selectedPlace]);

  const handleStartDateChange = (value) => {
    setFilterOptions((prev) => ({
      ...prev,
      startDate: value || null,
    }));
    setDownloadAble(false);
  };

  const handleEndDateChange = (value) => {
    setFilterOptions((prev) => ({
      ...prev,
      endDate: value || null,
    }));
    setDownloadAble(false);
  };

  const shouldDisableDate = (date) => {
    if (!filterOptions.startDate) return false;
    const diffDays = Math.abs(
      (new Date(date) - new Date(filterOptions.startDate)) /
        (1000 * 60 * 60 * 24)
    );
    return diffDays > 31;
  };

  const handleGetReport = async () => {
    try {
      setDownloadAble(false);
      const payload = {
        ...filterOptions,
        startDate: moment(filterOptions.startDate)
          .format("YYYY-MM-DD")
          .toString(),
        endDate: moment(filterOptions.endDate).format("YYYY-MM-DD").toString(),
      };
      const res = await dispatch(getRevenueSummary(payload)).unwrap();
      if (res.success) {
        setReservations(res.data);
        setDownloadAble(true);
      }
    } catch (error) {
      console.log("error --->", error.message);
    }
  };

  const downloadExcel = () => {
    // Prepare data for the worksheet
    const wsData = [];

    // Add title
    wsData.push(["Revenue Summary Report"]);
    wsData.push([""]); // Empty row for spacing

    // Add headers
    const headers = [
      "Rate",
      ...reservations.data.map((item) => item.time),
      "Total Reservations",
      "Gross Revenue by Rate",
    ];
    wsData.push(headers);

    // Add rate data
    Object.keys(reservations.totals).forEach((key) => {
      const row = [key];
      reservations.data.forEach((item) => {
        const rateData = item.rates[key];
        row.push(rateData ? rateData.count : 0);
      });
      row.push(reservations.totals[key].count);
      row.push(reservations.totals[key].totalSum / 100);
      wsData.push(row);
    });

    // Add summary rows
    // Enhanced helper: allow placing totals in either of the last two columns
    const addSummaryRow = (label, values, options = {}) => {
      const { totalReservationsTotal, grossRevenueTotal } = options;
      const row = [label];
      values.forEach((value) => row.push(value));
      // Add cells for the last two columns: 'Total Reservations' and 'Gross Revenue by Rate'
      row.push(
        typeof totalReservationsTotal !== "undefined"
          ? totalReservationsTotal
          : ""
      );
      row.push(
        typeof grossRevenueTotal !== "undefined" ? grossRevenueTotal : ""
      );
      wsData.push(row);
    };

    // Add all summary rows
    // Show API totalReservation under the 'Total Reservations' column
    addSummaryRow(
      "Total Reservations",
      reservations.data.map((item) => item.totalCount),
      {
        totalReservationsTotal: reservations.totalReservation,
        grossRevenueTotal: (reservations.total / 100).toFixed(2),
      }
    );
    addSummaryRow(
      "% of Total Reservations",
      reservations.data.map((item) => `${item.percentage}%`)
    );
    addSummaryRow(
      "Total Revenue",
      reservations.data.map((item) => (item.totalRevenue / 100).toFixed(2)),
      { grossRevenueTotal: (reservations.total / 100).toFixed(2) }
    );
    addSummaryRow(
      "% of Total $ Value",
      reservations.data.map((item) => `${item.revenuePercent}%`)
    );
    addSummaryRow(
      "Total Service Fee",
      reservations.data.map((item) => (item.totalServiceFee / 100).toFixed(2))
    );
    addSummaryRow(
      "Total Revenue - Service Fee",
      reservations.data.map((item) =>
        ((item.totalRevenue - item.totalServiceFee) / 100).toFixed(2)
      )
    );

    // Create a new workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    ws["!cols"] = [
      { wch: 30 }, // Rate column width
      ...Array(headers.length - 1).fill({ wch: 15 }), // Other columns width
    ];

    // Add borders using cell styles
    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = { c: C, r: R };
        const cell_ref = XLSX.utils.encode_cell(cell_address);

        // If cell doesn't exist, create it
        if (!ws[cell_ref]) {
          ws[cell_ref] = { t: "s", v: "" };
        }

        // Initialize cell style if it doesn't exist
        if (!ws[cell_ref].s) {
          ws[cell_ref].s = {};
        }

        // Apply border style - using Excel's built-in border styles
        ws[cell_ref].s.border = {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        };
      }
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, "Revenue Summary");

    // Generate XLSX file and trigger download
    XLSX.writeFile(wb, "revenue_summary.xlsx", {
      bookType: "xlsx",
      bookSST: true,
    });
  };

  const downloadPdf = () => {
    if (!reservations || !reservations.data || reservations.data.length === 0)
      return;

    const fmtDate = new Date(filterOptions.startDate).toLocaleDateString();
    const headers = [
      "Rate",
      ...reservations.data.map((item) => item.time),
      "Total Reservations",
      "Gross Revenue by Rate",
    ];

    // Build body rows for rates
    const body = Object.keys(reservations.totals || {}).map((key) => {
      const row = [key];
      reservations.data.forEach((item) => {
        const rateData = item.rates[key];
        row.push(rateData ? rateData.count : 0);
      });
      row.push(reservations.totals[key].count);
      row.push((reservations.totals[key].totalSum / 100).toFixed(2));
      return row;
    });

    // Summary rows (match Excel)
    const totalReservationsRow = [
      "Total Reservations",
      ...reservations.data.map((item) => item.totalCount),
      reservations.totalReservation,
      (reservations.total / 100).toFixed(2),
    ];
    const percentOfTotalReservationsRow = [
      "% of Total Reservations",
      ...reservations.data.map((item) => `${item.percentage}%`),
      "",
      "",
    ];
    const totalRevenueRow = [
      "Total Revenue",
      ...reservations.data.map((item) => (item.totalRevenue / 100).toFixed(2)),
      "",
      (reservations.total / 100).toFixed(2),
    ];
    const percentOfTotalValueRow = [
      "% of Total $ Value",
      ...reservations.data.map((item) => `${item.revenuePercent}%`),
      "",
      "",
    ];
    const totalServiceFeeRow = [
      "Total Service Fee",
      ...reservations.data.map((item) =>
        (item.totalServiceFee / 100).toFixed(2)
      ),
      "",
      "",
    ];
    const netRevenueRow = [
      "Total Revenue - Service Fee",
      ...reservations.data.map((item) =>
        ((item.totalRevenue - item.totalServiceFee) / 100).toFixed(2)
      ),
      "",
      "",
    ];

    const doc = new jsPDF({ orientation: "landscape", format: "a3" });
    const title = `${
      reservations?.placeData?.parkingCode || "Unknown"
    } Revenue Summary`;
    doc.setFontSize(18);
    doc.text(title, 14, 18);
    doc.setFontSize(12);
    doc.text(`Date: ${fmtDate}`, 14, 28);

    autoTable(doc, {
      head: [headers],
      body: [
        ...body,
        totalReservationsRow,
        percentOfTotalReservationsRow,
        totalRevenueRow,
        percentOfTotalValueRow,
        totalServiceFeeRow,
        netRevenueRow,
      ],
      startY: 36,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [33, 150, 243] },
      didParseCell: (data) => {
        if (data.section !== "head" && data.column.index > 0) {
          data.cell.styles.halign = "right";
        }
      },
    });

    const parkingCode = reservations?.placeData?.parkingCode || "Unknown";
    doc.save(`revenue_summary-${parkingCode}-${fmtDate}.pdf`);
  };

  const columns = useMemo(() => {
    const timeColumns = reservations.data?.map((item) => ({
      field: item.time,
      headerName: item.time,
      width: 100,
      type: "number",
    }));

    return [
      { field: "rate", headerName: "Rate", width: 170 },
      ...(timeColumns || []),
      {
        field: "totalReservations",
        headerName: "Total Reservations",
        width: 150,
        type: "number",
      },
      {
        field: "grossRevenue",
        headerName: "Gross Revenue by Rate",
        width: 150,
        type: "number",
      },
    ];
  }, [reservations]);

  const rows = useMemo(() => {
    if (!reservations.totals) return [];

    const rateRows = Object.keys(reservations.totals).map((key, index) => {
      const timeData = reservations.data?.map((item) => {
        const rateData = item.rates[key];
        return rateData ? { [item.time]: rateData.count } : { [item.time]: 0 };
      });

      return {
        id: `${key}-${index}`,
        rate: key,
        ...Object.assign({}, ...timeData),
        totalReservations: reservations.totals[key].count,
        grossRevenue: `$${amountToShow(reservations.totals[key].totalSum)}`,
      };
    });

    const totalReservationsRow = {
      id: "total-reservations",
      rate: "Total Reservations",
    };

    const percentageRow = {
      id: "percentage",
      rate: "% of Total Reservations",
    };

    const totalRevenueRow = {
      id: "total-revenue",
      rate: "Total Revenue",
    };

    const revenuePercentRow = {
      id: "revenue-percent",
      rate: "% of Total $ Value",
    };

    const serviceFeeRow = {
      id: "service-fee",
      rate: "Total Service Fee",
    };

    const netRevenueRow = {
      id: "net-revenue",
      rate: "Total Revenue - Service Fee",
    };

    reservations.data?.forEach((item) => {
      totalReservationsRow[item.time] = item.totalCount;
      percentageRow[item.time] = `${item.percentage}%`;
      totalRevenueRow[item.time] = `$${amountToShow(item.totalRevenue)}`;
      revenuePercentRow[item.time] = `${item.revenuePercent}%`;
      serviceFeeRow[item.time] = `$${amountToShow(item.totalServiceFee)}`;
      netRevenueRow[item.time] = `$${amountToShow(
        item.totalRevenue - item.totalServiceFee
      )}`;
    });

    // Set the 'Total Reservations' column value using API's totalReservation
    totalReservationsRow.totalReservations = reservations.totalReservation;
    // Place overall Total Revenue at Row 'Total Reservations' x Column 'Gross Revenue by Rate'
    totalReservationsRow.grossRevenue = `$${amountToShow(reservations.total)}`;

    return [
      ...rateRows,
      totalReservationsRow,
      percentageRow,
      totalRevenueRow,
      revenuePercentRow,
      serviceFeeRow,
      netRevenueRow,
    ];
  }, [reservations]);

  return (
    <>
      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={notification.close}
        bgWhite
      />
      <MDBox>
        <Grid container spacing={2}>
          {/* Row 1: Place + Timezone */}
          <Grid item xs={12} md={10}>
            <FormControl fullWidth>
              <PlacePicker />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <Autocomplete
                disableClearable
                name="tz"
                value={filterOptions.tz}
                options={timeZones}
                onChange={(event, newValue) => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    tz: newValue,
                  }));
                }}
                freeSolo
                size="small"
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="contained"
                      label={option}
                      {...getTagProps({ index })}
                    />
                  ))
                }
                renderInput={(params) => (
                  <MDInput
                    label="Select Timezone"
                    fullWidth={true}
                    {...params}
                  />
                )}
              />
            </FormControl>
          </Grid>
          {/* Row 2: Start Date + End Date */}
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <DatePicker
                id="startDate"
                placeholder="Start Date"
                size="md"
                format="MM/dd/yyyy"
                ranges={[]}
                value={
                  filterOptions.startDate
                    ? new Date(filterOptions.startDate)
                    : null
                }
                onChange={handleStartDateChange}
                onClean={() => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    startDate: null,
                  }));
                  setDownloadAble(false);
                }}
                shouldDisableDate={(date) => {
                  if (!filterOptions.endDate) return false;
                  const diffDays = Math.abs(
                    (new Date(date) - new Date(filterOptions.endDate)) /
                      (1000 * 60 * 60 * 24)
                  );
                  return diffDays > 31;
                }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <DatePicker
                id="endDate"
                placeholder="End Date"
                size="md"
                format="MM/dd/yyyy"
                ranges={[]}
                value={
                  filterOptions.endDate ? new Date(filterOptions.endDate) : null
                }
                onChange={handleEndDateChange}
                onClean={() => {
                  setFilterOptions((prev) => ({
                    ...prev,
                    endDate: null,
                  }));
                  setDownloadAble(false);
                }}
                shouldDisableDate={shouldDisableDate}
              />
            </FormControl>
          </Grid>
          {/* Empty spacer */}
          <Grid item xs={false} md={2}></Grid>
          {/* Row 3: Actions */}
          <Grid item xs={12} md={4}>
            <MDBox
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
              sx={{ width: "100%", flexWrap: "nowrap" }}
            >
              <Tooltip title="Get report" arrow>
                <MDButton
                  size="small"
                  color={sidenavColor}
                  disabled={
                    filterOptions.startDate && filterOptions.endDate
                      ? false
                      : true
                  }
                  onClick={handleGetReport}
                >
                  Go
                </MDButton>
              </Tooltip>
              <Tooltip title=" Download the report in Excel format." arrow>
                <MDButton
                  color={sidenavColor}
                  disabled={!downloadAble}
                  onClick={downloadExcel}
                  size="small"
                >
                  Excel
                </MDButton>
              </Tooltip>
              <Tooltip title=" Download the report in PDF format." arrow>
                <MDButton
                  color={sidenavColor}
                  disabled={!downloadAble}
                  onClick={downloadPdf}
                  size="small"
                >
                  PDF
                </MDButton>
              </Tooltip>
            </MDBox>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox mt={2}>
        {reservations.data && reservations.data.length > 0 && (
          <MDDataGrid
            rows={rows}
            rowCount={rows.length}
            columns={columns}
            getRowId={(row) => JSON.stringify(row)}
            disableSelectionOnClick
            loading={loading}
            hideFooter
            hideFooterPagination
            hideFooterSelectedRowCount
            // page={page}
            // pageSize={pageSize}
            // paginationMode="server"
            // paginationModel={{
            //   page: page,
            //   pageSize: pageSize,
            // }}
            components={{
              NoRowsOverlay: EmptyTableRowOverlay,
              LoadingOverlay: LinearProgress,
            }}
            componentsProps={{
              toolbar: {
                showQuickFilter: false,
                quickFilterProps: {
                  debounceMs: 1000,
                },
              },
            }}
            // autoHeight
            getRowClassName={(params) => {
              if (
                params.row.id === "total-reservations" ||
                params.row.id === "total-revenue" ||
                params.row.id === "revenue-percent" ||
                params.row.id === "service-fee" ||
                params.row.id === "net-revenue"
              ) {
                return "total-reservations-row";
              }
              return "";
            }}
          />
        )}
      </MDBox>
      <CircularIndeterminate
        type="full"
        size={20}
        text="Please wait..."
        open={loading}
      />
    </>
  );
}

export default RevenueSummary;
